import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { getAllTechniques } from "../../../../../services/techniquesServices";

import {
  getUserFavorites,
  addFavorite,
  removeFavorite,
} from "../../../../../services/usersTechniquesServices";

import {
  DEFAULT_TECHNIQUES_UI_STATE,
  STORAGE_KEY,
  buildDynamicOptions,
  createShareUrl,
  filterAndSortTechniques,
  getStoredUserId,
  readInitialUiState,
} from "../utils/techniquesUtils";

export function useTechniquesDashboard() {
  const [allTechniques, setAllTechniques] = useState([]);
  const [loading, setLoading] = useState(true);

  const [favorites, setFavorites] = useState([]);
  const [shareCopied, setShareCopied] = useState(false);

  const [openedTechnique, setOpenedTechnique] = useState(null);

  const [ui, setUi] = useState(() => readInitialUiState());
  const [debouncedSearch, setDebouncedSearch] = useState(ui.search || "");

  const userId = useMemo(() => getStoredUserId(), []);

  const persistUi = useCallback((nextUi) => {
    const safeNext = {
      ...DEFAULT_TECHNIQUES_UI_STATE,
      ...nextUi,
    };

    setUi(safeNext);

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(safeNext));
    } catch {
      // localStorage non disponible
    }
  }, []);

  const updateUi = useCallback(
    (patch, options = {}) => {
      const { resetPage = true } = options;

      setUi((previous) => {
        const next = {
          ...previous,
          ...patch,
          perPage:
            resetPage && !Object.prototype.hasOwnProperty.call(patch, "perPage")
              ? DEFAULT_TECHNIQUES_UI_STATE.perPage
              : patch.perPage ?? previous.perPage,
        };

        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
        } catch {
          // localStorage non disponible
        }

        return next;
      });
    },
    []
  );

  const resetFilters = useCallback(() => {
    persistUi(DEFAULT_TECHNIQUES_UI_STATE);
    setDebouncedSearch(DEFAULT_TECHNIQUES_UI_STATE.search);
  }, [persistUi]);

  const loadTechniques = useCallback(async () => {
    try {
      setLoading(true);

      const data = await getAllTechniques();
      setAllTechniques(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erreur chargement techniques :", error);
      toast.error("Erreur lors du chargement des techniques.");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadFavorites = useCallback(async () => {
    if (!userId) return;

    try {
      const favs = await getUserFavorites(userId);

      const ids = (Array.isArray(favs) ? favs : [])
        .map((favorite) => favorite?.technique_id ?? favorite?.id)
        .filter(Boolean);

      setFavorites(Array.from(new Set(ids)));
    } catch (error) {
      console.error("Erreur chargement favoris techniques :", error);
    }
  }, [userId]);

  useEffect(() => {
    loadTechniques();
  }, [loadTechniques]);

  useEffect(() => {
    loadFavorites();
  }, [loadFavorites]);

  useEffect(() => {
    const timer = setTimeout(() => {
      updateUi({ search: debouncedSearch });
    }, 300);

    return () => clearTimeout(timer);
  }, [debouncedSearch, updateUi]);

  const dynamicOptions = useMemo(
    () => buildDynamicOptions(allTechniques),
    [allTechniques]
  );

  const filteredTechniques = useMemo(() => {
    return filterAndSortTechniques({
      techniques: allTechniques,
      ui,
      favorites,
      familyOrder: dynamicOptions.familyOrder,
    });
  }, [allTechniques, ui, favorites, dynamicOptions.familyOrder]);

  const visibleTechniques = useMemo(
    () => filteredTechniques.slice(0, ui.perPage),
    [filteredTechniques, ui.perPage]
  );

  const stats = useMemo(() => {
    const familySet = new Set(
      allTechniques.map((technique) => technique?.famille).filter(Boolean)
    );

    const gradeSet = new Set(
      allTechniques.map((technique) => technique?.ceinture).filter(Boolean)
    );

    return {
      total: allTechniques.length,
      filtered: filteredTechniques.length,
      visible: visibleTechniques.length,
      favorites: favorites.length,
      families: familySet.size,
      grades: gradeSet.size,
    };
  }, [allTechniques, filteredTechniques, visibleTechniques, favorites.length]);

  const loadMore = useCallback(() => {
    updateUi(
      {
        perPage: ui.perPage + DEFAULT_TECHNIQUES_UI_STATE.perPage,
      },
      { resetPage: false }
    );
  }, [ui.perPage, updateUi]);

  const toggleFavorite = useCallback(
    async (techniqueId) => {
      if (!userId) {
        toast.info("Connectez-vous pour gérer vos favoris.");
        return;
      }

      const isFavorite = favorites.includes(techniqueId);

      setFavorites((previous) =>
        isFavorite
          ? previous.filter((id) => id !== techniqueId)
          : [...previous, techniqueId]
      );

      try {
        if (isFavorite) {
          await removeFavorite(userId, techniqueId);
          toast.success("Technique retirée des favoris.");
        } else {
          await addFavorite(userId, techniqueId);
          toast.success("Technique ajoutée aux favoris.");
        }
      } catch (error) {
        console.error("Erreur favori technique :", error);

        setFavorites((previous) =>
          isFavorite
            ? [...previous, techniqueId]
            : previous.filter((id) => id !== techniqueId)
        );

        toast.error("Erreur lors de la mise à jour du favori.");
      }
    },
    [userId, favorites]
  );

  const openTechnique = useCallback((technique) => {
    setOpenedTechnique(technique);
  }, []);

  const closeTechnique = useCallback(() => {
    setOpenedTechnique(null);
  }, []);

  const copyShareLink = useCallback(async () => {
    try {
      const url = createShareUrl(ui);

      await navigator.clipboard.writeText(url);

      setShareCopied(true);
      toast.success("Lien copié dans le presse-papiers.");

      window.setTimeout(() => setShareCopied(false), 1500);
    } catch (error) {
      console.error("Erreur partage lien techniques :", error);
      toast.error("Impossible de copier le lien.");
    }
  }, [ui]);

  return {
    allTechniques,
    filteredTechniques,
    visibleTechniques,
    dynamicOptions,
    stats,

    loading,
    favorites,
    userId,

    ui,
    updateUi,
    debouncedSearch,
    setDebouncedSearch,

    openedTechnique,
    openTechnique,
    closeTechnique,

    toggleFavorite,
    resetFilters,
    loadMore,

    copyShareLink,
    shareCopied,

    refresh: loadTechniques,
  };
}

export default useTechniquesDashboard;